import { useCallback } from 'react'

import { Flex, SimpleGrid, Stack, Text } from '@mantine/core'
import { IconMatrix, IconUsers } from '@tabler/icons-react'
import { useTranslate } from 'react-admin'
import { UserApiDTOScopes, UserScopesApiDTO } from '../../codegen/generated/Api.schemas'
import { LXLoader } from '../../components/LXLoader'
import { LXNoData } from '../../components/LXNoData'
import { LXYoutubeIcon } from '../../components/LXYoutubeIcon'
import { UpdateUserScopes } from './UpdateUserScopes'
import { UserScopesValues } from './UserScopesValues'

type UserScopesProps = {
  scopes?: UserApiDTOScopes
  readonly?: boolean
  onUpdate?: (scopes: UserScopesApiDTO) => void
}

export const UserScopes = ({ scopes, readonly, onUpdate }: UserScopesProps) => {
  const t = useTranslate()

  const getScopesValues = useCallback(
    (key: keyof UserScopesApiDTO, _scopes: string[]) => {
      if (!_scopes[0]) {
        return <LXNoData ta="left" my="xs" />
      }

      if (_scopes[0] === '*') {
        return <Text fs="italic">{t('_generics.all_male')}</Text>
      }

      return <UserScopesValues scopeKey={key} scopes={_scopes} />
    },
    [t],
  )

  const getScopeTitle = useCallback(
    (key: keyof UserApiDTOScopes) => {
      const icon = (() => {
        switch (key) {
          case 'cms':
            return <IconMatrix size={14} />
          case 'beneficiariesIds':
            return <IconUsers size={14} />
          case 'youtubeChannelsIds':
            return <LXYoutubeIcon />

          default:
            break
        }
      })()

      return (
        <Flex justify="flex-start" align="center" gap={8} w="fit-content">
          {icon}
          <Text pt={3} size="sm">
            {t(`pages.users.scopeKeys.${key}`)}
          </Text>
        </Flex>
      )
    },
    [t],
  )

  if (!scopes) {
    return <LXLoader />
  }

  if (Object.keys(scopes ?? {}).length === 0) {
    return <LXNoData ta="left" my="xs" />
  }

  return (
    <>
      <SimpleGrid cols={3}>
        {Object.entries(scopes).map(([key, _scopes]) => (
          <Stack key={key} gap={6}>
            {getScopeTitle(key as keyof UserApiDTOScopes)}
            {readonly && getScopesValues(key as keyof UserScopesApiDTO, _scopes)}
          </Stack>
        ))}
      </SimpleGrid>
      {!readonly && onUpdate && (
        <UpdateUserScopes scopes={scopes as UserScopesApiDTO} onUpdate={onUpdate} />
      )}
    </>
  )
}
